/** Every response carries the id of the request it answers; `error` marks a failure,
 *  and `fatal` says the worker itself is done for — its script never loaded, say —
 *  rather than this one request being unserviceable. */
interface WorkerResponse {
  id: number
  error?: string
  fatal?: boolean
}

export interface WorkerRequestOptions {
  /** How many requests may be inside the worker at once: the bound on how many
   *  payloads `postMessage` has copied and the worker has not answered yet.
   *  Unbounded by default, which suits a client whose payloads are small. */
  maxInFlight?: number
}

export interface WorkerRequestClient<Req extends object, Res extends WorkerResponse> {
  /** The worker's answer, or `null` if it couldn't produce one. `abortSignal`
   *  drops the request: one still waiting for a slot is never posted at all. */
  tryRequest: (payload: Req, abortSignal?: AbortSignal) => Promise<Res | null>
  /** Abandons every request the worker is carrying and kills it; the next request
   *  starts a fresh one. For a cancelled pass, whose queued jobs would otherwise
   *  keep running and make the next pass wait behind them. */
  reset: () => void
}

/** A request dropped by `reset` or by its caller's abort. Answered as `null` like
 *  any other way the worker can fail to produce a result, but without the console
 *  noise — nothing failed, nobody wanted the answer any more. */
class WorkerRequestCancelled extends Error {}

/**
 * Request/response over a lazily started worker, for work the main thread can do
 * itself if it has to.
 *
 * `tryRequest` answers `null` for every way the worker can let us down — no
 * `Worker` in this context, construction threw, the worker died, the request
 * failed — so callers treat it as "do it yourself" rather than as an error, and a
 * missing or broken worker costs a fallback instead of the whole operation.
 *
 * Only a worker-level failure retires the worker: it died, or it answered `fatal`.
 * A request that simply couldn't be served — one corrupt photo among hundreds —
 * falls back on its own and leaves the worker to the rest, which is why there is
 * no failure count here. Retiring on those would put a whole batch back on the
 * main thread over one bad file.
 *
 * `maxInFlight` is the client's half of a worker that serializes its own work:
 * without it every caller posts immediately and the queue forms *inside* the
 * worker, holding a structured-clone copy of each payload — full-size image data
 * for as long as the queue is deep. Holding the extra requests here instead keeps
 * one copy per slot, and lets an abort drop a request that was never posted.
 *
 * `spawn` may load its worker on demand: `new Worker(new URL('./x.ts',
 * import.meta.url))` has to be written out literally for the bundler to find the
 * worker and emit it, and `import.meta` is unparsable to Jest's CommonJS
 * transform — so that line lives in its own module, which this only pulls in when
 * a worker is actually wanted.
 */
export function createWorkerRequestClient<Req extends object, Res extends WorkerResponse>(
  name: string,
  spawn: () => Worker | Promise<Worker>,
  { maxInFlight = Number.POSITIVE_INFINITY }: WorkerRequestOptions = {},
): WorkerRequestClient<Req, Res> {
  let worker: Worker | null = null
  let starting: Promise<Worker | null> | null = null
  let unavailable = false
  let nextRequestId = 0
  /** Bumped by `reset`, so a worker that was still starting when the pass was
   *  cancelled is thrown away instead of taking the dead one's place. */
  let generation = 0
  /** Settles `null` when `reset` ends the current generation. A request waiting for
   *  that generation's worker to start races this, so it lets go of its slot at the
   *  reset instead of whenever the cancelled startup gets round to settling —
   *  otherwise a full set of such requests keeps the next pass queued behind it. */
  let signalReset!: () => void
  let whenReset = new Promise<null>((resolve) => {
    signalReset = () => resolve(null)
  })
  const pending = new Map<number, { resolve: (result: Res) => void; reject: (error: Error) => void }>()

  /** Requests that hold a slot (posted, unanswered), and the grants of those still
   *  queued for one. */
  let inFlight = 0
  let waiting: Array<(granted: boolean) => void> = []

  /** Resolves true once this request may be posted, false if it was dropped first. */
  const acquireSlot = (abortSignal?: AbortSignal): Promise<boolean> => {
    if (inFlight < maxInFlight) {
      inFlight++
      return Promise.resolve(true)
    }
    return new Promise<boolean>((resolve) => {
      const grant = (granted: boolean) => {
        abortSignal?.removeEventListener('abort', onAbort)
        resolve(granted)
      }
      const onAbort = () => {
        const at = waiting.indexOf(grant)
        // Already granted — the request is the worker's problem now, not the queue's.
        if (at < 0) return
        waiting.splice(at, 1)
        grant(false)
      }
      waiting.push(grant)
      abortSignal?.addEventListener('abort', onAbort)
    })
  }

  /** Hands the slot straight to the next waiter, so `inFlight` never dips below
   *  the bound while work is queued. */
  const releaseSlot = () => {
    const next = waiting.shift()
    if (next) next(true)
    else inFlight--
  }

  /** Lets go of everything queued; each of those requests answers `null`. */
  const dropWaiting = () => {
    const dropped = waiting
    waiting = []
    for (const grant of dropped) grant(false)
  }

  const settlePending = (error: Error) => {
    const carried = [...pending.values()]
    pending.clear()
    for (const request of carried) request.reject(error)
  }

  const retire = (reason: Error) => {
    unavailable = true
    generation++
    worker?.terminate()
    worker = null
    dropWaiting()
    settlePending(reason)
  }

  const start = async (): Promise<Worker | null> => {
    const startedAt = generation
    try {
      const spawned = await spawn()
      // Cancelled while this was starting: this worker was never anybody's.
      if (startedAt !== generation) {
        spawned.terminate()
        return null
      }
      spawned.onmessage = (event: MessageEvent<Res>) => {
        const message = event.data
        const request = pending.get(message.id)
        if (!request) return
        pending.delete(message.id)
        if (!message.error) {
          request.resolve(message)
          return
        }
        request.reject(new Error(message.error))
        if (message.fatal) retire(new Error(`${name} worker cannot serve requests: ${message.error}`))
      }
      // Retiring rejects what the worker was carrying, so those requests fall back
      // rather than hang.
      spawned.onerror = () => retire(new Error(`${name} worker failed`))
      worker = spawned
      return worker
    } catch (error) {
      console.error(`${name}: no worker in this context`, error)
      unavailable = true
      return null
    }
  }

  /** One start at a time, however many requests arrive before it finishes. A
   *  context without `Worker` throws inside `spawn`, which `start` reports like any
   *  other failure to produce one. */
  const getWorker = (): Promise<Worker | null> => {
    if (unavailable) return Promise.resolve(null)
    if (worker) return Promise.resolve(worker)
    if (!starting) {
      const thisStart: Promise<Worker | null> = start().finally(() => {
        // A reset may already have detached this start and begun another.
        if (starting === thisStart) starting = null
      })
      starting = thisStart
    }
    return starting
  }

  return {
    async tryRequest(payload: Req, abortSignal?: AbortSignal): Promise<Res | null> {
      if (unavailable || abortSignal?.aborted) return null
      // Nothing large crosses the boundary until this resolves — and if the pass is
      // cancelled while it waits, nothing crosses at all.
      if (!(await acquireSlot(abortSignal))) return null
      try {
        // Only a request that has to wait for a start races the reset: one handed a
        // running worker would just pile reactions onto a promise that may never settle.
        const target = worker ?? (await Promise.race([getWorker(), whenReset]))
        if (!target || abortSignal?.aborted) return null
        const id = nextRequestId++
        try {
          return await new Promise<Res>((resolve, reject) => {
            pending.set(id, { resolve, reject })
            target.postMessage({ id, ...payload })
          })
        } catch (error) {
          pending.delete(id)
          if (error instanceof WorkerRequestCancelled) return null
          console.error(`${name}: request failed, falling back to the main thread`, error)
          return null
        }
      } finally {
        releaseSlot()
      }
    },

    reset() {
      generation++
      worker?.terminate()
      worker = null
      // A start still in flight belongs to the cancelled generation and will answer
      // `null`. Detach it, so the next pass's first request starts its own worker
      // instead of awaiting that one and falling back to the main thread.
      starting = null
      // Those awaiting it answer `null` and free their slots now, not when it settles.
      signalReset()
      whenReset = new Promise<null>((resolve) => {
        signalReset = () => resolve(null)
      })
      // Queued first: a dropped request must not be handed the slot a rejected one
      // is about to release.
      dropWaiting()
      settlePending(new WorkerRequestCancelled(`${name} worker was reset`))
    },
  }
}
